import { useMemo } from "react";
import { type ApolloError } from "@apollo/client";
import { useFeatureFlag } from "@jsq/flags-react";
import { useGetAccountingPayableForBatchCreationQuery } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import {
  AccountingPayableBatch,
  mapAccountingPayableToPositionRows,
} from "../adapters/accountingPayableAdapter";
import { PositionRowData } from "../types";

type UseAccountingPayableParams = {
  batchId: string | null;
};

type UseAccountingPayableResult = {
  positionRows: PositionRowData[];
  queryData: AccountingPayableBatch | null;
  loading: boolean;
  error: ApolloError | undefined;
  queryResult: ReturnType<typeof useGetAccountingPayableForBatchCreationQuery>;
};

/**
 * Hook that encapsulates fetching AccountingPayable batch data and transforming it into PositionRowData[].
 */
export const useAccountingPayable = ({
  batchId,
}: UseAccountingPayableParams): UseAccountingPayableResult => {
  const paymentsInstructionScreening = useFeatureFlag("paymentsInstructionScreening", false);
  const picrCasesEnabled = useFeatureFlag("picrCasesEnabled", false);

  const queryResult = useGetAccountingPayableForBatchCreationQuery({
    variables: {
      id: batchId ?? "",
      picrCasesEnabled,
    },
    context: clientContext(),
    skip: !batchId,
  });

  const { data, loading, error } = queryResult;

  const queryData = (data?.accountingPayable as AccountingPayableBatch) || null;

  const positionRows = useMemo<PositionRowData[]>(() => {
    if (!queryData) return [];
    return mapAccountingPayableToPositionRows(queryData, {
      paymentsInstructionScreening,
    });
  }, [queryData, paymentsInstructionScreening]);

  return { positionRows, queryData, loading, error, queryResult };
};
